
import React, { useState } from "react";
import ReactMarkdown from "react-markdown";
import { Message } from "@/types/message";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Database, FileCog, Cpu, CopyCheck } from "lucide-react";
import { Button } from "@/components/ui/button";

interface MessageContentProps {
  message: Message;
  showDebugInfo?: boolean;
  onToggleSystemPrompt?: () => void;
  isSystemPromptVisible?: boolean;
}

const MessageContent: React.FC<MessageContentProps> = ({
  message,
  showDebugInfo = false,
  onToggleSystemPrompt,
  isSystemPromptVisible,
}) => {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Determine if we should show the context menu
  // Only show for bot messages, if they're not error messages, AND showDebugInfo is true
  const showContextMenu = message.isBot && !message.error && showDebugInfo;

  const handleToggleSystemPrompt = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onToggleSystemPrompt) {
      onToggleSystemPrompt();
    }
  };

  return (
    <div className="w-full overflow-hidden">
      <div className="markdown-content">
        <ReactMarkdown>{message.content}</ReactMarkdown>
      </div>

      {showDebugInfo && message.isBot && (
        <div className="mt-2 text-xs text-gray-400 flex flex-wrap gap-2">
          {message.statusCode && (
            <span className="bg-gray-700 px-2 py-1 rounded text-white">
              Status: {message.statusCode}
            </span>
          )}
          {message.model && (
            <span className="bg-gray-700 px-2 py-1 rounded text-white">
              Model: {message.model}
            </span>
          )}
          {message.cached !== undefined && (
            <span 
              className={`px-2 py-1 rounded text-white ${
                message.cached ? "bg-green-700" : "bg-blue-700"
              }`}
            >
              {message.cached ? "Cached" : "API Response"}
            </span>
          )}
          {message.systemPrompt && (
            <Button
              onClick={handleToggleSystemPrompt}
              variant="outline"
              size="sm"
              className="bg-gray-700 px-2 py-1 h-auto rounded text-white hover:bg-gray-600 transition-colors"
            >
              {isSystemPromptVisible ? "Hide Prompt" : "Show Prompt"}
            </Button>
          )}
        </div>
      )}
      
      {/* Only show the Message options dropdown when debug mode is enabled */}
      {showContextMenu && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="text-xs text-gray-400 mt-2 hover:text-gray-600 transition-colors">
              Message options...
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            <DropdownMenuItem onClick={() => copyToClipboard(message.content)}>
              <CopyCheck className="mr-2 h-4 w-4" />
              {copied ? "Copied!" : "Copy Response"}
            </DropdownMenuItem>
            
            <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
              {message.cached ? (
                <>
                  <Database className="mr-2 h-4 w-4 text-green-500" />
                  <span>From Cache</span>
                </>
              ) : (
                <>
                  <Cpu className="mr-2 h-4 w-4 text-blue-500" />
                  <span>From API</span>
                </>
              )}
            </DropdownMenuItem>
            
            {message.model && (
              <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                <FileCog className="mr-2 h-4 w-4" />
                Model: {message.model}
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
};

export default MessageContent;
